import React, { useEffect, useState } from "react";

import {
  AlertTriangle,
  InfoIcon,
  MapPin,
  Paperclip,
  Truck,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { CreditProgress } from "~/components/ui/credit-progress";
import { Label } from "~/components/ui/label";
import { Separator } from "~/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { Switch } from "~/components/ui/switch";
import { CreditUnlockAlert } from "~/components/CreditUnlockAlert";
import { useClientAddresses } from "~/hooks/useClientAddresses";
import { useClientCreditUnlock } from "~/hooks/useClientCreditUnlock";
import { supabase } from "~/integrations/supabase/client";
import { useCreditUtilization } from "~/pages/clients/components/client-detail/hooks/useCreditUtilization";
import ClientFinancialInfo from "~/pages/clients/components/client-detail/purchase-order/ClientFinancialInfo";
import AddressSelector from "~/pages/clients/components/client-detail/purchase-order/components/AddressSelector";
import CommentsSection from "~/pages/clients/components/client-detail/purchase-order/components/CommentsSection";
import DeliveryDateSelector from "~/pages/clients/components/client-detail/purchase-order/components/DeliveryDateSelector";
import OrderFooter from "~/pages/clients/components/client-detail/purchase-order/components/OrderFooter";
import OrderSummary from "~/pages/clients/components/client-detail/purchase-order/components/OrderSummary";
import ProductSelector from "~/pages/clients/components/client-detail/purchase-order/components/ProductSelector";
import { useOrderForm } from "~/pages/clients/components/client-detail/purchase-order/hooks/useOrderForm";
import DocumentUploadForm from "~/pages/purchase-orders/components/sales-request/DocumentUploadForm";
import { useProfileStore } from "~/store/profileStore";
import { Client, SalesRequestDocument } from "~/types/client";
import { isPickupAddress } from "~/utils/addressUtils";
import { validate7DayCreditEligibility } from "~/utils/creditUtilizationUtils";
import { formatCurrency } from "~/utils/helpers";

interface CreatePurchaseOrderDrawerProps {
  client: Client;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CreatePurchaseOrderDrawer: React.FC<CreatePurchaseOrderDrawerProps> = ({
  client,
  open,
  onOpenChange,
}) => {
  const { role } = useProfileStore();
  const isSeller = role === "seller";
  const [salesConditionName, setSalesConditionName] = useState<string | null>(
    null
  );
  const [salesConditionMaxDays, setSalesConditionMaxDays] = useState<
    number | null
  >(null);
  const [has7DayUnpaidOrder, setHas7DayUnpaidOrder] = useState(false);
  const [isLoading7DayCheck, setIsLoading7DayCheck] = useState(false);
  const [documents, setDocuments] = useState<SalesRequestDocument[]>([]);

  // Hook for checking/managing 7-day credit unlock status
  const {
    isUnlocked: is7DayCreditUnlocked,
    isLoading: isUnlockLoading,
    refetch: refetchUnlock,
  } = useClientCreditUnlock(String(client.id), "7day_credit_overdue");

  const {
    addresses,
    isLoading: addressesLoading,
    fetchAddresses,
  } = useClientAddresses(client.id.toString());
  const {
    creditData,
    loading: creditLoading,
    fetchCreditUtilization,
  } = useCreditUtilization(String(client.id));
  const {
    selectedAddressId,
    setSelectedAddressId,
    orderItems,
    total,
    editingItem,
    comments,
    setComments,
    internalNotes,
    setInternalNotes,
    deliveryDate,
    setDeliveryDate,
    requestedDeliveryDateStart,
    requestedDeliveryDateEnd,
    setRequestedDeliveryDateStart,
    setRequestedDeliveryDateEnd,
    resetForm,
    handleAddProduct,
    handleEditItem,
    handleRemoveItem,
    handleSaveOrder,
    isFormValid,
    isSubmitting,
    hasPriceBelowSuggested,
    facturaObservations,
    setFacturaObservations,
    isNonSale,
    setIsNonSale,
    setDocuments: setOrderFormDocuments,
    fleteRequired,
    hasFleteInOrder,
    refetchOverdueUnlock,
  } = useOrderForm(client);

  // Who can mark an order as "no venta" (muestra / traslado). Facturación (Mafe)
  // plus admins. See docs/business-rules + CLAUDE.md access decision.
  const canCreateNonSale =
    role === "facturacion" || role === "admin" || role === "superadmin";

  // Fetch the client's sales condition and check if it's a 7-day credit condition
  useEffect(() => {
    const checkSalesCondition = async () => {
      if (!client.id || !open) return;

      try {
        // Get sales condition ID
        const { data: clientData, error: clientError } = await supabase
          .from("clients")
          .select("sales_condition_id")
          .eq("id", String(client.id))
          .single();

        if (clientError || !clientData?.sales_condition_id) {
          setSalesConditionName(null);
          setSalesConditionMaxDays(null);
          return;
        }

        // Get sales condition name and maximum_days
        const { data: conditionData, error: conditionError } = await supabase
          .from("sales_conditions")
          .select("name, maximum_days")
          .eq("id", clientData.sales_condition_id)
          .single();

        if (!conditionError && conditionData) {
          setSalesConditionName(conditionData.name);
          setSalesConditionMaxDays(conditionData.maximum_days);

          // Check if it's a 7-day credit condition (either by name or days)
          const is7DayCredit =
            conditionData.name === "CREDITO A 7 DIAS" ||
            conditionData.maximum_days === 7;

          // If it's the 7-day credit condition, check for overdue payments
          if (is7DayCredit && isSeller) {
            // Skip validation if client has an active unlock
            if (is7DayCreditUnlocked) {
              setHas7DayUnpaidOrder(false);
            } else {
              setIsLoading7DayCheck(true);

              try {
                // Use the new validation function that checks for overdue payments only
                const validation = await validate7DayCreditEligibility(
                  String(client.id),
                  conditionData.maximum_days
                );

                // Set the state based on whether the client can create new orders
                setHas7DayUnpaidOrder(!validation.canCreate);
              } catch (error) {
                console.error(
                  "Error checking 7-day credit eligibility:",
                  error
                );
                // Fallback to more permissive behavior on error
                setHas7DayUnpaidOrder(false);
              }

              setIsLoading7DayCheck(false);
            }
          }
        }
      } catch (err) {
        console.error("Error checking sales condition:", err);
      }
    };

    checkSalesCondition();
  }, [client.id, open, isSeller, is7DayCreditUnlocked, isUnlockLoading]);

  useEffect(() => {
    if (open) {
      fetchAddresses();
      resetForm();
      // Refetch unlock status to ensure we have the latest data
      refetchOverdueUnlock?.();
    }
  }, [open]);

  useEffect(() => {
    if (addresses.length > 0) {
      const defaultAddress = addresses.find((addr) => addr.isDefault);
      setSelectedAddressId(defaultAddress?.id || addresses[0].id);
    }
  }, [addresses]);

  // Document state debugging
  useEffect(() => {
    console.log("CreatePurchaseOrderDrawer document state updated:", documents);
  }, [documents]);

  // Update the OrderForm's documents when our documents state changes
  useEffect(() => {
    if (setOrderFormDocuments) {
      console.log("CreatePurchaseOrderDrawer: Documents changed", documents);
      // Make sure we're passing a new array reference to trigger updates
      setOrderFormDocuments([...documents]);
    }
  }, [documents, setOrderFormDocuments]);

  // Reset documents when drawer opens
  useEffect(() => {
    if (open) {
      console.log("CreatePurchaseOrderDrawer: Reset documents on drawer open");
      setDocuments([]);
    }
  }, [open]);

  const handleSaveAndMaybeRefetch = () => {
    handleSaveOrder((status?: "pending" | "prepaid") => {
      onOpenChange(false);
      // NOTE: No automatic approval - credit utilization refresh is not needed since orders don't auto-approve
      // Credit utilization will be updated when manually approved later
    });
  };

  const utilizationPercentage = creditData.percentage;

  // Check if the client has a 7-day credit condition (either by name or maximum_days value)
  const is7DayCredit =
    salesConditionName === "CREDITO A 7 DIAS" || salesConditionMaxDays === 7;

  const showCreditLimitWarning = is7DayCredit && total > 1000000;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex h-full w-[100vw] flex-col overflow-hidden p-0 sm:max-w-[600px]"
      >
        <SheetHeader className="mb-4 flex-shrink-0 px-4 pt-6">
          <SheetTitle className="text-xl sm:text-2xl">
            {isNonSale ? "Nueva Guía de Traslado (No Venta)" : "Nueva Nota de Venta"}
          </SheetTitle>
          <SheetDescription className="mt-1 text-sm">
            Cliente: {client.company}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-grow overflow-y-auto px-4">
          <Alert className="mb-4 p-3">
            <MapPin className="h-4 w-4" />
            <AlertTitle>Permiso de ubicación requerido</AlertTitle>
            <AlertDescription>
              Para crear una nota de venta, es necesario acceder a su ubicación
              actual. Se le solicitará permiso al guardar.
            </AlertDescription>
          </Alert>

          {canCreateNonSale && (
            <div className="mb-4 rounded-lg border bg-muted/40 p-3 sm:p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="no-venta-toggle"
                    className="text-sm font-medium"
                  >
                    No es una venta (muestra / traslado)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Emite una guía “Otros traslados no venta” que rebaja el
                    stock. No genera factura ni deuda.
                  </p>
                </div>
                <Switch
                  id="no-venta-toggle"
                  checked={isNonSale}
                  onCheckedChange={setIsNonSale}
                />
              </div>
              {isNonSale && (
                <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                  <InfoIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" />
                  <span>
                    La nota quedará aprobada directamente (sin autorización de
                    cobranza). Genere la guía de traslado para rebajar el stock;
                    no podrá facturarse.
                  </span>
                </div>
              )}
            </div>
          )}

          {is7DayCredit && (
            <>
              {(has7DayUnpaidOrder || is7DayCreditUnlocked) ? (
                <CreditUnlockAlert
                  clientId={String(client.id)}
                  unlockType="7day_credit_overdue"
                  title="No se puede crear nota de venta"
                  description="Este cliente tiene pagos vencidos con crédito a 7 días. Debe cancelar los pagos vencidos antes de crear nuevas notas de venta."
                  onUnlockChange={(unlocked) => {
                    if (unlocked) {
                      setHas7DayUnpaidOrder(false);
                    } else {
                      // Re-run validation when unlock is revoked
                      refetchUnlock();
                    }
                  }}
                />
              ) : (
                <Alert className="mb-3 border-amber-200 bg-amber-50 px-2 py-2 sm:mb-6 sm:px-4 sm:py-4">
                  <InfoIcon className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-sm sm:text-base">
                    Política de crédito a 7 días
                  </AlertTitle>
                  <AlertDescription className="text-xs sm:text-sm">
                    Este cliente tiene condición de crédito a 7 días. Las
                    ordenes hasta $1.000.000 se aprueban automáticamente.
                    Órdenes por montos mayores requerirán aprobación manual.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {showCreditLimitWarning && (
            <Alert className="mb-3 border-yellow-200 bg-yellow-50 px-2 py-2 sm:mb-6 sm:px-4 sm:py-4">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertTitle className="text-sm sm:text-base">
                Requiere aprobación manual
              </AlertTitle>
              <AlertDescription className="text-xs sm:text-sm">
                El monto total de {formatCurrency(total)} excede el límite de
                $1.000.000 para aprobación automática con crédito a 7 días. Esta
                orden quedará en estado pendiente para revisión.
              </AlertDescription>
            </Alert>
          )}

          {hasPriceBelowSuggested && isSeller && (
            <Alert className="mb-3 border-yellow-200 bg-yellow-50 px-2 py-2 sm:mb-6 sm:px-4 sm:py-4">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertTitle className="text-sm sm:text-base">
                Precios por debajo de los sugeridos
              </AlertTitle>
              <AlertDescription className="text-xs sm:text-sm">
                Ha establecido precios por debajo de los sugeridos para uno o
                más productos. Esta orden requerirá aprobación manual
                independientemente del crédito disponible.
              </AlertDescription>
            </Alert>
          )}

          <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50 p-2 sm:mb-6 sm:p-4">
            {isSeller ? (
              // For sellers, only show the sales condition info
              <ClientFinancialInfo client={client} />
            ) : (
              // For admins and operations, show both financial info and sales condition
              <>
                <h3 className="mb-1 text-base font-medium text-blue-800 sm:mb-2 sm:text-lg">
                  Información Financiera
                </h3>

                <div className="mb-2 mt-1 space-y-1 sm:mb-4 sm:mt-3 sm:space-y-2">
                  <div className="mb-1 flex items-center justify-between text-xs sm:text-sm">
                    <span className="font-medium">Utilización de Crédito</span>
                    <span>{utilizationPercentage.toFixed(1)}%</span>
                  </div>
                  <CreditProgress
                    value={utilizationPercentage}
                    className="h-1.5 sm:h-2"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground sm:text-xs">
                    <span>
                      Disponible: {formatCurrency(creditData.available)}
                    </span>
                    <span>
                      Límite: {formatCurrency(client.insuredAmount || 0)}
                    </span>
                  </div>
                  {creditData.totalPendingAmount > 0 && (
                    <div className="mt-2 rounded-lg bg-gray-50 p-2">
                      <div className="flex justify-between text-[10px] sm:text-xs">
                        <span className="font-medium text-gray-700">
                          Total Pendiente:
                        </span>
                        <span className="font-semibold text-red-600">
                          {formatCurrency(creditData.totalPendingAmount)}
                        </span>
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-2 text-[9px] text-gray-600 sm:text-xs">
                        <div>
                          <span>Notas de Venta: </span>
                          <span className="font-medium">
                            {formatCurrency(creditData.salesRequestUtilized)}
                          </span>
                        </div>
                        <div>
                          <span>Facturas Manuales: </span>
                          <span className="font-medium">
                            {formatCurrency(creditData.manualFacturasUtilized)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <ClientFinancialInfo client={client} />
              </>
            )}
          </div>

          <Separator className="my-3 sm:my-6" />

          <div className="mb-3 rounded-lg border border-amber-100 bg-amber-50 p-2 sm:mb-6 sm:p-4">
            <h3 className="mb-1 text-base font-medium text-amber-800 sm:mb-2 sm:text-lg">
              Información de Entrega
            </h3>

            <div className="mb-2 sm:mb-4">
              <h4 className="mb-1 text-xs font-medium text-amber-700 sm:mb-2 sm:text-sm">
                Dirección de Entrega
              </h4>
              <AddressSelector
                addresses={addresses}
                selectedAddressId={selectedAddressId}
                onSelect={setSelectedAddressId}
                isLoading={addressesLoading}
                clientId={client.id.toString()}
                fetchAddresses={fetchAddresses}
              />

              {/* FLETE Alert */}
              {fleteRequired && (
                <Alert
                  className={`mt-3 ${
                    hasFleteInOrder
                      ? "border-green-200 bg-green-50"
                      : "border-orange-200 bg-orange-50"
                  }`}
                >
                  <Truck
                    className={`h-4 w-4 ${
                      hasFleteInOrder ? "text-green-600" : "text-orange-600"
                    }`}
                  />
                  <AlertTitle
                    className={`text-sm ${
                      hasFleteInOrder ? "text-green-800" : "text-orange-800"
                    }`}
                  >
                    {hasFleteInOrder ? "Flete incluido" : "Flete requerido"}
                  </AlertTitle>
                  <AlertDescription
                    className={`text-xs ${
                      hasFleteInOrder ? "text-green-700" : "text-orange-700"
                    }`}
                  >
                    {hasFleteInOrder
                      ? "El producto FLETE ha sido agregado a la orden por estar fuera de la Región Metropolitana, Valparaíso y O'Higgins."
                      : "Esta dirección requiere flete por estar fuera de la Región Metropolitana, Valparaíso y O'Higgins. El FLETE se agregará automáticamente."}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div>
              <h4 className="mb-1 text-xs font-medium text-amber-700 sm:mb-2 sm:text-sm">
                Fecha de Entrega
              </h4>
              <DeliveryDateSelector
                deliveryDate={deliveryDate}
                requestedDeliveryDateStart={requestedDeliveryDateStart}
                requestedDeliveryDateEnd={requestedDeliveryDateEnd}
                setDeliveryDate={setDeliveryDate}
                setRequestedDeliveryDateStart={setRequestedDeliveryDateStart}
                setRequestedDeliveryDateEnd={setRequestedDeliveryDateEnd}
                isPickup={isPickupAddress(selectedAddressId)}
              />
            </div>
          </div>

          <Separator className="my-3 sm:my-6" />

          <div className="mb-3 rounded-lg border border-green-100 bg-green-50 p-2 sm:mb-6 sm:p-4">
            <h3 className="mb-1 text-base font-medium text-green-800 sm:mb-3 sm:text-lg">
              Productos
            </h3>
            <ProductSelector
              onAddProduct={handleAddProduct}
              editingItem={editingItem}
            />
          </div>

          {orderItems.length > 0 && (
            <div className="mb-3 rounded-lg border border-purple-100 bg-purple-50 p-2 sm:mb-6 sm:p-4">
              <h3 className="mb-1 text-base font-medium text-purple-800 sm:mb-3 sm:text-lg">
                Resumen de la Orden
              </h3>
              <OrderSummary
                items={orderItems}
                onRemoveItem={handleRemoveItem}
                onEditItem={handleEditItem}
                total={total}
              />
            </div>
          )}

          <Separator className="my-3 sm:my-6" />

          <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-2 sm:mb-6 sm:p-4">
            <CommentsSection
              comments={comments}
              setComments={setComments}
              internalNotes={internalNotes}
              setInternalNotes={setInternalNotes}
              facturaObservations={facturaObservations}
              setFacturaObservations={setFacturaObservations}
            />
          </div>

          {/* Document Upload Section */}
          <div className="mb-6 mt-6">
            <h3 className="mb-3 flex items-center text-lg font-medium">
              <Paperclip className="mr-2 h-5 w-5 text-gray-500" />
              Documentos Adjuntos
            </h3>
            <DocumentUploadForm
              isCreating={true}
              onDocumentsChanged={(updatedDocs) => {
                console.log(
                  "DocumentUploadForm provided documents:",
                  updatedDocs
                );
                // Ensure we're updating with a new array reference
                setDocuments([...updatedDocs]);
              }}
            />
          </div>

          <div className="h-24"></div>
        </div>

        <div className="mt-auto flex-shrink-0 border-t bg-gray-50 px-4 py-4">
          <OrderFooter
            total={total}
            isFormValid={isFormValid() && !has7DayUnpaidOrder}
            isSubmitting={isSubmitting}
            onSave={handleSaveAndMaybeRefetch}
            availableCredit={creditData.available}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CreatePurchaseOrderDrawer;
